import React, { useEffect, useState } from "react";
import { Calendar, Clock, CheckCircle, AlertCircle, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_API_KEY = "test_key_123";

export const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiKey = window.localStorage.getItem("apiKey") || DEMO_API_KEY;

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/bookings`, {
          headers: { "X-API-Key": apiKey }
        });
        if (res.ok) {
          const data = await res.json();
          setBookings(data || []);
        } else {
          setError("Unable to load bookings");
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load bookings");
      } finally {
        setLoading(false);
      }
    };
    loadBookings();
  }, [apiKey]);

  const renderProposedSlots = (booking) => {
    if (!booking.proposed_slots || booking.proposed_slots.length === 0)
      return "-";
    return booking.proposed_slots
      .map((slot) =>
        slot.start ? new Date(slot.start).toLocaleString() : "Unknown"
      )
      .join(" • ");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Bookings
          </h1>
          <p className="text-slate-500 mt-1">
            Appointment proposals and confirmed bookings created by your AI receptionist.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
          <Calendar className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-700 text-sm font-medium">{bookings.length} Total</span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!error && bookings.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
            <Calendar className="w-8 h-8 text-emerald-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings yet</h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            When callers schedule appointments, you'll see proposed slots and confirmations here.
          </p>
        </div>
      )}

      {bookings.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="max-h-[540px] overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-white border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Created
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Service
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Confirmed For
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Proposed Slots
                  </th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr
                    key={booking._id || booking.id}
                    className="border-b border-slate-200/60 hover:bg-white"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {booking.created_at
                        ? new Date(booking.created_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {booking.service_type || booking.service_code || "N/A"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <span
                        className={[
                          "px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border",
                          booking.status === "confirmed"
                            ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                            : booking.status === "proposed"
                            ? "bg-sky-500/10 text-sky-300 border-sky-500/40"
                            : "bg-slate-100 text-slate-700 border-slate-700",
                        ].join(" ")}
                      >
                        {booking.status || "pending"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {booking.scheduled_for
                        ? new Date(
                            booking.scheduled_for
                          ).toLocaleString()
                        : "Not yet"}
                    </td>
                    <td className="px-3 py-2 text-slate-400 max-w-xs">
                      {renderProposedSlots(booking)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
